import React, { Fragment } from 'react'
import * as Portal from '@radix-ui/react-portal'
import { Combobox } from '@headlessui/react'
import { startCase } from 'lodash'

import {
  calcPriceWithDiscount,
  getCouponSuggestionDurationText,
  getIsValidPlanDiscount,
  getIsValidProductDiscount,
  showDiscountByType,
} from 'modules/ProductInfo/utils/utils'
import { SuggestionContainer, SuggestionItem } from 'common/components/Suggestions/Suggestions'
import { MIN_LIFETIME_PLAN_DISCOUNTED_PRICE, MIN_NON_ZERO_DISCOUNTED_PRICE } from '../../constants/priceConstants'
import { CURRENCY_DISPLAY, CURRENCY_SYMBOL } from 'common/constants/currencyConstants'

export default function CouponsSuggestions({
  coupons,
  searchValue,
  product,
  inputRef,
  popperContainer,
  currency,
  productType,
}) {
  const suggestions = Object.entries(coupons).filter(
    ([_, coupon]) =>
      coupon?.stripeStatus !== 'pending' &&
      coupon?.stripeStatus !== 'error' &&
      (coupon.name.toLowerCase().includes(searchValue.toLowerCase()) ||
        coupon.id.toLowerCase().includes(searchValue.toLowerCase())) &&
      !product?.promo?.find((promo) => promo.stripeCouponId === coupon.id) &&
      (productType === 'plan'
        ? getIsValidPlanDiscount({ plan: product, coupon })
        : getIsValidProductDiscount({ product, coupon }))
  )
  const inputWidth = inputRef?.current?.getBoundingClientRect()?.width
  return (
    <Portal.Root>
      <Combobox.Options className='z-[999]' ref={popperContainer} modal={false}>
        <SuggestionContainer containerStyle={{ width: inputWidth }} scrollAreaClasses='!max-h-52'>
          {!coupons || !suggestions.length ? (
            <li className='cursor-default select-none py-2 px-2'>No coupons found</li>
          ) : (
            suggestions.map(([couponKey, coupon]) => (
              <Combobox.Option as={Fragment} key={couponKey} value={couponKey}>
                {({ active }) => (
                  <SuggestionItem active={active}>
                    <div className='flex'>
                      <div className='flex flex-col flex-1 px-2.5'>
                        <p className='text-sm font-bold mb-1'>{coupon.name || 'Untitled coupon'}</p>
                        <span className='text-xs font-medium text-gray-500'>
                          {`Code: ${coupon.id}` || '(couponid-autogenerated)'}
                        </span>
                      </div>
                      <div className='flex flex-col items-center px-2 min-w-[90px]'>
                        <p className='text-sm font-bold mb-1'>Type</p>
                        <span className='text-xs font-medium text-gray-500'>{startCase(coupon.type)}</span>
                      </div>
                      <div className='flex flex-col items-center px-2 min-w-[90px]'>
                        <p className='text-sm font-bold mb-1'>Duration</p>
                        <span className='text-xs font-medium text-gray-500'>
                          {getCouponSuggestionDurationText({ coupon })}
                        </span>
                      </div>
                      <div className='flex flex-col items-center px-2 min-w-[90px]'>
                        <p className='text-sm font-bold mb-1'>Discount</p>
                        <span className='text-xs font-medium text-gray-500'>
                          {showDiscountByType({ type: coupon.type, discount: coupon.discount, currency })}
                        </span>
                      </div>
                      <div className='flex flex-col items-center px-2 min-w-[90px]'>
                        <p className='text-sm font-bold mb-1'>Result price</p>
                        <span className='bg-tGreen text-xs font-medium text-white rounded-md py-0.5 px-1 uppercase'>
                          {CURRENCY_SYMBOL[currency]}
                          {(
                            calcPriceWithDiscount({
                              type: coupon.type,
                              discount: coupon.discount,
                              price: product.price,
                            }) / 100
                          ).toFixed(2)}{' '}
                          {currency}
                        </span>
                      </div>
                    </div>
                  </SuggestionItem>
                )}
              </Combobox.Option>
            ))
          )}
          {/* extra space to account for absolutely positioned hint */}
          <div className='h-4' />
          <div className='absolute bottom-0 inset-x-0 px-2 py-0.5 bg-white text-xs text-gray-500'>
            {getHintText({
              isLifetimePurchase: product?.isLifetimePurchase,
              isOneTimeProduct: productType === 'oneTimeProduct',
              currency,
            })}
          </div>
        </SuggestionContainer>
      </Combobox.Options>
    </Portal.Root>
  )
}

function getHintText({ isLifetimePurchase, isOneTimeProduct, currency }) {
  if (isLifetimePurchase) {
    return `Coupons that are trial only or result in a price lower than ${(
      MIN_LIFETIME_PLAN_DISCOUNTED_PRICE / 100
    ).toFixed(2)}${CURRENCY_DISPLAY[currency]} are not
            allowed for lifetime plans.`
  } else {
    return `Discounted price must be free or at least ${(MIN_NON_ZERO_DISCOUNTED_PRICE / 100).toFixed(
      2
    )}${CURRENCY_DISPLAY[currency]}${isOneTimeProduct ? ' for one time products' : ''}.`
  }
}
